import { useState } from "react";
import { Star } from "lucide-react";

import type { MyReviewState } from "../../lib/marketplace-connection-api";
import {
  reviewAskView,
  reviewPostedMessage,
  reviewVisibilityNote,
  reviewWriteFailure,
  type ReviewAnswer,
  type ReviewWriteOutcome,
} from "../../lib/review-ask";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { ReviewAskDialog } from "./ReviewAskDialog";
import {
  closingReviewActionLabel,
  reviewActionLabel,
  reviewedOnLabel,
  type ProRole,
} from "./ReviewedProAction.helpers";

/**
 * ReviewedProAction — the review affordance on a pro's card (Your Team, the
 * relationship page, Home's review prompt), for BOTH agents and lenders.
 *
 * It renders from the SERVER's ask (`next_ask`, via `reviewAskView`) and
 * never decides on its own (review stage rules, 2026-10-09):
 *
 *   capture  -> "Review your agent" opens the worked_together sheet.
 *   close    -> "Add your closing review" opens the close-upgrade modal,
 *               pre-filled from the existing review.
 *   readonly -> "View review" opens a READ-ONLY dialog. There is no edit
 *               control: reviews are final once posted.
 *   none     -> nothing.
 *
 * Every submit ends in a visible notice: the confirmation on success, or
 * "You've already reviewed {name}" when the server refuses a repeat (409),
 * never a silent drop.
 */

export interface ReviewedProActionProps {
  proName: string;
  proUserId: string;
  /** The pro's role ("agent" | "lo"); anything else falls back to neutral copy. */
  role?: ProRole;
  /** The server's review state for this pair; undefined while loading. */
  state: MyReviewState | null | undefined;
  /** Writes the answer (see `submitReviewAnswer`) and refreshes `state`.
   *  Omitted (or no proUserId) renders nothing at all. */
  onSubmit?: (answer: ReviewAnswer) => Promise<void>;
  /** Tenure in whole weeks, when known — omitted drops the clause entirely. */
  weeksTogether?: number;
  incentiveLabel?: string;
  disabled?: boolean;
}

export function ReviewedProAction({
  proName,
  proUserId,
  role,
  state,
  onSubmit,
  weeksTogether,
  incentiveLabel,
  disabled,
}: ReviewedProActionProps) {
  const [askOpen, setAskOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [notice, setNotice] = useState<ReviewWriteOutcome | null>(null);

  if (!onSubmit || !proUserId) return null;

  const view = reviewAskView(state);

  const submit = async (answer: ReviewAnswer) => {
    setAskOpen(false);
    try {
      await onSubmit(answer);
      setNotice({ kind: "posted", message: reviewPostedMessage(proName) });
    } catch (error) {
      setNotice(reviewWriteFailure(error, proName));
    }
  };

  // counts_after rides the refreshed state read (GET reviewDTO), so the note
  // follows both the post-submit state and the read-only view.
  const visibilityNote = reviewVisibilityNote(state?.review, new Date(), proName);
  const visibilityLine = visibilityNote ? (
    <p className="text-sm text-muted-foreground" data-testid="review-visibility-note">
      {visibilityNote}
    </p>
  ) : null;

  const noticeLine = notice ? (
    <p
      className={`text-sm ${notice.kind === "failed" ? "text-destructive" : "text-muted-foreground"}`}
      data-testid="review-notice"
      data-outcome={notice.kind}
      role="status"
    >
      {notice.message}
    </p>
  ) : null;

  if (view.kind === "readonly") {
    const review = view.review;
    return (
      <>
        {noticeLine}
        {visibilityLine}
        <Button
          className="h-10 w-full justify-center gap-1.5 rounded-xl sm:h-9 sm:w-auto"
          data-testid="team-view-review-button"
          onClick={() => setViewOpen(true)}
          size="sm"
          variant="ghost"
        >
          <Star aria-hidden="true" className="h-4 w-4" />
          View review
        </Button>

        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          {/* DialogContent takes only its declared props — a data-testid
              passed here is silently dropped, so it lives on the body. */}
          <DialogContent
            description={reviewedOnLabel(review)}
            title={`Your review of ${proName}`}
          >
            <div className="space-y-4 py-2" data-testid="review-readonly-dialog">
              <div
                aria-label={`${review.stars} out of 5 stars`}
                className="flex gap-1"
                data-testid="review-readonly-stars"
                role="img"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <span
                    aria-hidden="true"
                    // Filled stars carry the brand colour, matching the pro's
                    // received-reviews card. All-grey read as disabled rather
                    // than as a rating.
                    className={`text-2xl leading-none ${
                      review.stars >= value ? "text-primary" : "text-muted-foreground/40"
                    }`}
                    key={value}
                  >
                    {review.stars >= value ? "★" : "☆"}
                  </span>
                ))}
              </div>

              {review.body ? (
                <p
                  className="text-sm text-foreground"
                  data-testid="review-readonly-body"
                >
                  {review.body}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  You left a rating without a written note.
                </p>
              )}

              <Button
                className="w-full"
                onClick={() => setViewOpen(false)}
                type="button"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (view.kind === "none") return noticeLine;

  return (
    <>
      {noticeLine}
      {notice?.kind === "posted" ? visibilityLine : null}
      <Button
        className="h-10 w-full justify-center gap-1.5 rounded-xl sm:h-9 sm:w-auto"
        data-ask={view.kind}
        data-testid="team-review-button"
        disabled={disabled}
        onClick={() => setAskOpen(true)}
        size="sm"
        variant="ghost"
      >
        <Star aria-hidden="true" className="h-4 w-4" />
        {view.kind === "close" ? closingReviewActionLabel() : reviewActionLabel(role)}
      </Button>

      {askOpen && (
        <ReviewAskDialog
          incentiveLabel={incentiveLabel}
          onClose={() => setAskOpen(false)}
          onSubmit={(answer) => void submit(answer)}
          open
          proName={proName}
          view={view}
          weeksTogether={weeksTogether}
        />
      )}
    </>
  );
}
